import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// Counter animation component
const AnimatedCounter = ({ end, duration = 2000, delay = 0, prefix = "", suffix = "" }: {
  end: number;
  duration?: number;
  delay?: number;
  prefix?: string;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      const startTime = Date.now();
      const startCount = 0;

      const updateCount = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(startCount + (end - startCount) * easeOutQuart);
        
        setCount(currentCount);

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        } else {
          setCount(end);
        }
      };

      requestAnimationFrame(updateCount);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, end, duration, delay]);

  return (
    <div ref={counterRef} className="text-4xl font-bold text-purple-600 mb-2">
      {prefix}{count}{suffix}
    </div>
  );
};

const GenerativeAICourse = () => {
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Enroll in Generative AI Expert Program",
    subtitle: "Start your journey to mastering Generative AI and creative coding",
    formType: "course_enrollment",
    interestedProgram: "Become a Generative AI Expert: Unlocking Creativity with Code"
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Enroll in Generative AI Expert Program",
      subtitle: config.subtitle || "Start your journey to mastering Generative AI and creative coding",
      formType: config.formType || "course_enrollment",
      interestedProgram: config.interestedProgram || "Become a Generative AI Expert: Unlocking Creativity with Code"
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  // Cursor tracker effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const tracker = document.getElementById('cursor-tracker');
      const section = document.querySelector('.cursor-tracker-section');
      
      if (tracker && section) {
        const rect = section.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Only track if cursor is within the section
        if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
          tracker.style.left = `${x - 8}px`; // Offset by half the tracker size
          tracker.style.top = `${y - 8}px`;
          tracker.style.opacity = '0.8';
        } else {
          tracker.style.opacity = '0';
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <style>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll-left {
          animation: scroll-left var(--animation-duration, 45s) linear infinite;
        }
        
        @keyframes counter {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>
      
    <div className="min-h-screen bg-white">
      <Header openPopup={openPopup} />
      
        {/* Original Hero Section from GenerativeAI */}
      <section className="relative bg-gray-900 text-white min-h-screen flex items-center justify-center overflow-hidden">
        {/* Sophisticated AI Background */}
        <div className="absolute inset-0">
          {/* Base dark background */}
          <div className="absolute inset-0 bg-gray-900"></div>
          
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-cyan-600/10 animate-pulse"></div>
          
          {/* Dynamic mesh background */}
          <div className="absolute inset-0 opacity-20">
            <svg width="100%" height="100%" className="absolute inset-0">
              <defs>
                <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#3B82F6" strokeWidth="0.5" opacity="0.3"/>
                </pattern>
                <radialGradient id="glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity="0.3"/>
                  <stop offset="100%" stopColor="#6366F1" stopOpacity="0"/>
                </radialGradient>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
              <circle cx="20%" cy="30%" r="100" fill="url(#glow)" className="animate-pulse delay-100" />
              <circle cx="80%" cy="70%" r="150" fill="url(#glow)" className="animate-pulse delay-300" />
              <circle cx="60%" cy="20%" r="80" fill="url(#glow)" className="animate-pulse delay-500" />
            </svg>
          </div>
          
          {/* Floating AI Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Code Matrix Effect */}
            <div className="absolute top-10 left-10 font-mono text-xs text-blue-400/40 leading-relaxed">
              <div className="animate-pulse delay-100">{'{'}</div>
              <div className="animate-pulse delay-200 ml-2">"model": "gpt-4",</div>
              <div className="animate-pulse delay-300 ml-2">"temperature": 0.7,</div>
              <div className="animate-pulse delay-400 ml-2">"max_tokens": 2048</div>
              <div className="animate-pulse delay-500">{'}'}</div>
            </div>

            {/* AI Brain Visualization */}
            <div className="absolute top-20 right-20">
              <div className="relative w-32 h-32">
                <svg width="128" height="128" className="animate-spin" style={{ animationDuration: '30s' }}>
                  <g fill="none" stroke="#6366F1" strokeWidth="1" opacity="0.6">
                    <circle cx="64" cy="64" r="60" strokeDasharray="5,5" />
                    <circle cx="64" cy="64" r="45" strokeDasharray="3,3" />
                    <circle cx="64" cy="64" r="30" strokeDasharray="2,2" />
                  </g>
                  <g fill="#3B82F6" opacity="0.8">
                    <circle cx="64" cy="40" r="3" className="animate-pulse delay-100" />
                    <circle cx="40" cy="64" r="3" className="animate-pulse delay-200" />
                    <circle cx="88" cy="64" r="3" className="animate-pulse delay-300" />
                    <circle cx="64" cy="88" r="3" className="animate-pulse delay-400" />
                  </g>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-2xl">ðŸ§ </div>
                </div>
              </div>
            </div>

            {/* Generative Art Brushes */}
            <div className="absolute bottom-32 left-16">
              <svg width="200" height="100" className="opacity-60">
                <defs>
                  <linearGradient id="brush1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0"/>
                    <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.8"/>
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <path d="M20,50 Q60,20 120,40 Q160,60 180,30" stroke="url(#brush1)" strokeWidth="8" fill="none" strokeLinecap="round" className="animate-pulse delay-200"/>
                <path d="M10,70 Q80,80 140,60 Q170,50 190,70" stroke="#06B6D4" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.7" className="animate-pulse delay-400"/>
              </svg>
            </div>

            {/* Floating Code Blocks */}
            <div className="absolute bottom-40 right-32 font-mono text-xs space-y-2">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded px-3 py-2 border border-blue-500/20 animate-pulse delay-100">
                <span className="text-cyan-400">const</span> <span className="text-white">ai</span> <span className="text-cyan-400">=</span> <span className="text-green-400">new</span> <span className="text-yellow-400">OpenAI</span><span className="text-white">()</span>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded px-3 py-2 border border-purple-500/20 animate-pulse delay-300">
                <span className="text-purple-400">await</span> <span className="text-white">model</span><span className="text-cyan-400">.</span><span className="text-yellow-400">generate</span><span className="text-white">(prompt)</span>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded px-3 py-2 border border-green-500/20 animate-pulse delay-500">
                <span className="text-green-400">image</span> <span className="text-cyan-400">=</span> <span className="text-white">diffusion</span><span className="text-cyan-400">.</span><span className="text-yellow-400">create</span><span className="text-white">()</span>
              </div>
            </div>

            {/* Neural Network Connections */}
            <div className="absolute top-1/2 left-20 transform -translate-y-1/2">
              <svg width="150" height="200" className="opacity-40">
                <g stroke="#6366F1" strokeWidth="1" fill="none">
                  <path d="M20,50 Q60,30 100,60 Q120,80 140,50" className="animate-pulse delay-100" strokeDasharray="3,3"/>
                  <path d="M20,100 Q80,80 120,120 Q140,140 140,100" className="animate-pulse delay-300" strokeDasharray="3,3"/>
                  <path d="M20,150 Q60,170 100,140 Q130,120 140,150" className="animate-pulse delay-500" strokeDasharray="3,3"/>
                </g>
                <g fill="#3B82F6">
                  <circle cx="20" cy="50" r="4" className="animate-bounce delay-100" />
                  <circle cx="20" cy="100" r="4" className="animate-bounce delay-200" />
                  <circle cx="20" cy="150" r="4" className="animate-bounce delay-300" />
                  <circle cx="140" cy="50" r="4" className="animate-bounce delay-400" />
                  <circle cx="140" cy="100" r="4" className="animate-bounce delay-500" />
                  <circle cx="140" cy="150" r="4" className="animate-bounce delay-600" />
                </g>
              </svg>
            </div>

            {/* Particle System */}
            <div className="absolute inset-0">
              <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400 rounded-full animate-ping delay-100"></div>
              <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-purple-400 rounded-full animate-ping delay-300"></div>
              <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping delay-500"></div>
              <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-indigo-400 rounded-full animate-ping delay-700"></div>
              <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-blue-300 rounded-full animate-ping delay-900"></div>
            </div>

            {/* AI Output Examples */}
            <div className="absolute top-1/3 right-10 space-y-3">
              <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-3 border border-blue-500/20 max-w-xs animate-pulse delay-200">
                <div className="text-xs text-blue-300 mb-1">Generated Text</div>
                <div className="text-xs text-gray-300">"The future of AI is creative collaboration..."</div>
              </div>
              <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-3 border border-purple-500/20 max-w-xs animate-pulse delay-400">
                <div className="text-xs text-purple-300 mb-1">AI Art</div>
                <div className="w-full h-8 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded"></div>
              </div>
              <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-3 border border-cyan-500/20 max-w-xs animate-pulse delay-600">
                <div className="text-xs text-cyan-300 mb-1">Audio Wave</div>
                <div className="flex items-end space-x-1 h-6">
                  <div className="w-1 bg-cyan-400 h-2 animate-pulse"></div>
                  <div className="w-1 bg-cyan-400 h-4 animate-pulse delay-100"></div>
                  <div className="w-1 bg-cyan-400 h-6 animate-pulse delay-200"></div>
                  <div className="w-1 bg-cyan-400 h-3 animate-pulse delay-300"></div>
                  <div className="w-1 bg-cyan-400 h-5 animate-pulse delay-400"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-12 pt-2 pb-8">
          <div className="text-center">
          {/* Program Badge */}
          <div className="inline-block bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl px-6 py-3 mb-8 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="white"/>
                    <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="text-green-300 font-semibold text-sm">AI EXPERT</span>
              </div>
              <span className="text-white font-bold text-sm">1to10x PROGRAM</span>
            </div>
          </div>
          
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="text-gray-300">10X Your</span> <span className="text-white">Generative AI Career</span><br />
            <span className="text-gray-300">with</span> <span className="bg-gradient-to-r from-green-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">Creative AI Solutions</span>
          </h1>
          
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            <span className="text-yellow-400 font-semibold">Go from beginner to confident creator</span> <span className="text-white">in 3 months with hands-on training.</span> <span className="text-blue-400 font-semibold">Build practical AI projects with guided support.</span>
          </p>
          
          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12 max-w-5xl mx-auto">
            <div className="bg-blue-600/20 border border-blue-500/30 rounded-xl p-4 text-center backdrop-blur-sm">
              <div className="flex justify-center mb-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 14L22 9L12 4L2 9L12 14Z" fill="#60A5FA"/>
                  <path d="M12 14L22 9V16L12 21L2 16V9L12 14Z" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="11" r="2" fill="white"/>
                </svg>
              </div>
              <div className="text-white font-semibold text-sm">AI EXPERT TRACK</div>
              <div className="text-gray-300 text-xs mt-1">Upon Completion</div>
            </div>
            <div className="bg-purple-600/20 border border-purple-500/30 rounded-xl p-4 text-center backdrop-blur-sm">
              <div className="flex justify-center mb-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#A855F7" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="text-white font-semibold text-sm">3 MONTHS</div>
              <div className="text-gray-300 text-xs mt-1">Intensive Training</div>
            </div>
            <div className="bg-green-600/20 border border-green-500/30 rounded-xl p-4 text-center backdrop-blur-sm">
              <div className="flex justify-center mb-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="#10B981"/>
                </svg>
              </div>
              <div className="text-white font-semibold text-sm">25+ PROJECTS</div>
              <div className="text-gray-300 text-xs mt-1">Real-World Experience</div>
            </div>
            <div className="bg-yellow-600/20 border border-yellow-500/30 rounded-xl p-4 text-center backdrop-blur-sm">
              <div className="flex justify-center mb-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" fill="#F59E0B"/>
                </svg>
              </div>
              <div className="text-white font-semibold text-sm">AI CREATIVE</div>
              <div className="text-gray-300 text-xs mt-1">Status Unlocked</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a 
              href="https://academy.10xdataanalyst.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 hover:shadow-xl inline-block"
            >
              Enrol Now
            </a>
            
            <button 
              onClick={() => openPopup({
                title: "Book FREE Strategy Call",
                subtitle: "Connect with our career transformation experts to discuss your goals and create a personalized roadmap",
                formType: "strategy_call"
              })}
              className="border-2 border-gray-400 text-gray-300 hover:bg-gray-700 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:scale-105 backdrop-blur-sm"
            >
              Book FREE Strategy Call
            </button>
          </div>

          {/* Rating */}
          <div className="text-center">
            <p className="text-gray-300">
              This program is rated as <span className="text-yellow-400 font-semibold">excellent</span> by 92% of AI enthusiasts
            </p>
            </div>
          </div>
        </div>
      </section>

        {/* What is Generative AI Section */}
        <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                What is <span className="text-purple-600">Generative AI</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                The revolutionary technology that's reshaping every industry by creating human-like content across text, images, audio, and code
            </p>
          </div>

            {/* Main Definition */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    The Future of <span className="text-purple-600">Creative Intelligence</span>
                  </h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Generative AI uses advanced machine learning models to create new, original content that mimics human creativity. 
                    From writing compelling stories to generating stunning artwork, composing music, and even coding applications - 
                    it's the technology that's democratizing creativity and productivity.
                  </p>
                  <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                    <div className="flex items-center gap-2 font-semibold mb-2 text-purple-800">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      Key Insight
                  </div>
                    <p className="text-sm text-purple-700 leading-relaxed">
                      Generative AI doesn't just automate tasks - it amplifies human creativity, enabling anyone to produce 
                      professional-quality content across multiple domains.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
                      <div className="flex justify-center mb-3">
                        <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                        </svg>
                      </div>
                      <div className="font-semibold text-gray-900 mb-1">Text</div>
                      <div className="text-xs text-gray-600">Articles, Stories, Code</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
                      <div className="flex justify-center mb-3">
                        <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                        </svg>
                      </div>
                      <div className="font-semibold text-gray-900 mb-1">Images</div>
                      <div className="text-xs text-gray-600">Art, Photos, Designs</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
                      <div className="flex justify-center mb-3">
                        <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        </svg>
                      </div>
                      <div className="font-semibold text-gray-900 mb-1">Audio</div>
                      <div className="text-xs text-gray-600">Music, Voice, Sounds</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-gray-200">
                      <div className="flex justify-center mb-3">
                        <svg className="w-8 h-8 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                        </svg>
                      </div>
                      <div className="font-semibold text-gray-900 mb-1">Code</div>
                      <div className="text-xs text-gray-600">Apps, Scripts, Tools</div>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Market Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
              <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">Market Opportunity</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <AnimatedCounter 
                    end={109} 
                    duration={2500} 
                    delay={0} 
                    prefix="$" 
                    suffix="B" 
                  />
                  <div className="text-gray-700 font-medium">Market Size by 2030</div>
                  <div className="text-sm text-gray-600 mt-1">43.8% CAGR Growth</div>
                </div>
                <div className="text-center">
                  <AnimatedCounter 
                    end={85} 
                    duration={2500} 
                    delay={500} 
                    suffix="%" 
                  />
                  <div className="text-gray-700 font-medium">AI Adoption Momentum</div>
                  <div className="text-sm text-gray-600 mt-1">Across modern teams</div>
                </div>
                <div className="text-center">
                  <AnimatedCounter 
                    end={150} 
                    duration={2500} 
                    delay={1000} 
                    suffix="+"
                  />
                  <div className="text-gray-700 font-medium">Creative AI Tools</div>
                  <div className="text-sm text-gray-600 mt-1">Across modern workflows</div>
                </div>
              </div>
            </div>

            {/* Career Opportunities */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  High-Demand <span className="text-purple-600">Career Paths</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">AI Product Manager</div>
                      <div className="text-gray-600 text-sm">$120K - $180K annually</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/>
                      </svg>
                  </div>
                  <div>
                      <div className="font-semibold text-gray-900">Generative AI Engineer</div>
                      <div className="text-gray-600 text-sm">$130K - $200K annually</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">AI Consultant</div>
                      <div className="text-gray-600 text-sm">$100 - $500 per hour</div>
                </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11H7v9a2 2 0 002 2h8a2 2 0 002-2h-2a1 1 0 01-1 1H10a1 1 0 01-1-1v-9zm3-6V3a1 1 0 011-1h2a1 1 0 011 1v2h2a1 1 0 011 1v2a1 1 0 01-1 1h-2v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V9h-2a1 1 0 01-1-1V6a1 1 0 011-1h2z"/>
                      </svg>
                  </div>
                  <div>
                      <div className="font-semibold text-gray-900">AI Startup Founder</div>
                      <div className="text-gray-600 text-sm">Unlimited earning potential</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Learn Now?</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 text-xs font-bold">
                      1
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Early Adopter Advantage</div>
                      <div className="text-gray-600 text-sm">Position yourself as an expert before the market saturates</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 text-xs font-bold">
                      2
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Technology Maturity</div>
                      <div className="text-gray-600 text-sm">Tools are now accessible and powerful enough for practical applications</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 text-xs font-bold">
                      3
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Industry Transformation</div>
                      <div className="text-gray-600 text-sm">Every sector is integrating AI - be ready to lead the change</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 mt-1 text-xs font-bold">
                      4
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Creative Revolution</div>
                      <div className="text-gray-600 text-sm">Combine technical skills with creativity for unique career opportunities</div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Benefits */}
      <section className="relative py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        {/* Separator Line */}
        <div className="absolute top-0 left-0 w-full h-px bg-gray-300"></div>
        
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The 5-Stage <span className="text-purple-600">Creative AI Mastery</span> System
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From Creative Beginner to AI Artist - your complete creative transformation in 3 months
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
            {[
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                ),
                title: "Creative Beginner â†’ AI Writer",
                description: "Master ChatGPT, Claude, and GPT-4 for content creation. Learn prompt engineering, style control, and automated writing workflows to produce engaging blogs, scripts, and marketing copy."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                ),
                title: "AI Writer â†’ AI Visual Artist",
                description: "Create stunning visuals with DALL-E, Midjourney, and Stable Diffusion. Master advanced prompting, style transfer, and image editing to produce professional artwork and designs."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "AI Artist â†’ Multimedia Creator",
                description: "Generate music with Suno AI, create videos with RunwayML, and produce podcasts with voice synthesis. Build complete multimedia campaigns and interactive experiences."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: "Creator â†’ AI Entrepreneur",
                description: "Package your creative AI skills into profitable services. Build client portfolios, create subscription offerings, and launch your creative AI agency or consultancy."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: "10x Creative AI Master",
                description: "Think creativity-first, build multi-modal AI solutions at scale, and lead the creative revolution in your industry. You're no longer using AI tools - you're crafting the future of creativity."
              },
              {
                icon: (
                  <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
                title: "Global Creative AI Community",
                description: "Join 1000+ creative professionals across 15+ countries, access exclusive creative showcases, and become part of the next generation of AI-powered artists and creators."
              }
            ].map((benefit, idx) => (
              <div key={idx} className="text-left">
                {/* Icon */}
                <div className="mb-6">
                  {benefit.icon}
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Curriculum - 8 Sprints */}
      <section className="relative">
        <div className="w-full h-px bg-gray-300 absolute top-0 left-0"></div>
        
        <div className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                The <span className="text-purple-600">6-Module</span> Creative AI Mastery Roadmap
              </h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                3 months. 6 comprehensive modules. Master every aspect of generative AI and become a creative AI expert.
              </p>
                      </div>

            {/* Curriculum Accordion */}
            <div className="max-w-5xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {[
                  {
                    month: "MONTH 1: TEXT & CONTENT MASTERY",
                    title: "Module 1-2: Master AI Writing & Content Creation",
                    sprints: [
                      {
                        name: "Module 1: AI Writing Fundamentals",
                        lessons: [
                          "ChatGPT-4, Claude 3.5, and Gemini Pro mastery",
                          "Advanced prompt engineering for content creation",
                          "Style control, tone matching, and voice consistency",
                          "Long-form content generation (blogs, articles, ebooks)",
                          "Creative writing: stories, scripts, and screenplays",
                          "Technical writing: documentation, tutorials, guides",
                          "Marketing copy: ads, sales pages, email campaigns",
                          "SEO-optimized content creation and keyword integration"
                        ]
                      },
                      {
                        name: "Module 2: Content Automation & Workflows",
                        lessons: [
                          "Building content calendars with AI assistance",
                          "Multi-platform content adaptation (blog â†’ social â†’ video)",
                          "AI-powered research and fact-checking workflows", 
                          "Content personalization at scale",
                          "Brand voice training and custom GPTs",
                          "Content repurposing strategies across formats",
                          "AI editing, proofreading, and optimization",
                          "Building your content creation portfolio"
                        ]
                      }
                    ]
                  },
                  {
                    month: "MONTH 2: VISUAL & MULTIMEDIA CREATION",
                    title: "Module 3-4: Master AI Art, Video & Audio Creation", 
                    sprints: [
                      {
                        name: "Module 3: AI Visual Art & Design",
                        lessons: [
                          "DALL-E 3, Midjourney, and Stable Diffusion mastery",
                          "Advanced prompting for consistent visual styles",
                          "Character design and style reference techniques",
                          "Logo design, branding, and marketing visuals",
                          "Photo editing and enhancement with AI",
                          "Creating illustrations, digital art, and NFTs",
                          "AI-powered graphic design workflows",
                          "Building visual brand guidelines with AI"
                        ]
                      },
                      {
                        name: "Module 4: Video & Audio Production",
                        lessons: [
                          "RunwayML, Pika Labs, and video generation tools",
                          "AI voice synthesis with ElevenLabs and Murf",
                          "Music generation with Suno AI and AIVA",
                          "Video editing automation and enhancement",
                          "Podcast creation and audio post-production",
                          "AI avatars and virtual presenters",
                          "Creating viral social media content",
                          "Multi-modal storytelling and campaigns"
                        ]
                      }
                    ]
                  },
                  {
                    month: "MONTH 3: BUSINESS & MONETIZATION",
                    title: "Module 5-6: Build Your Creative AI Business",
                    sprints: [
                      {
                        name: "Module 5: Creative AI Services & Products",
                        lessons: [
                          "Packaging AI skills into profitable services",
                          "Building client portfolios and case studies",
                          "Pricing strategies for creative AI work",
                          "Creating subscription and retainer models",
                          "Reusable creative AI workflow templates",
                          "Educational content and course asset creation",
                          "AI-powered prototype development",
                          "Portfolio packaging for creative projects"
                        ]
                      },
                      {
                        name: "Module 6: Scale & Growth",
                        lessons: [
                          "Building your personal project voice as an AI creator",
                          "Content planning and creative storytelling",
                          "Project presentation and feedback loops",
                          "Collaboration workflows for creative teams",
                          "Reusable asset libraries and workflow documentation",
                          "Workshop-style presentation practice",
                          "Creative production process design",
                          "Keeping your AI skills current"
                        ]
                      }
                    ]
                  }
                ].map((module, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`module-${idx}`}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
                  >
                    <AccordionTrigger className="px-8 py-6 text-left hover:no-underline hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4 w-full">
                        <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">{idx + 1}</span>
                    </div>
                        <div className="flex-1">
                          <div className="text-sm text-purple-600 font-semibold mb-1">{module.month}</div>
                          <h3 className="text-xl font-bold text-gray-900 text-left">
                            {module.title}
                          </h3>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-8 pt-4">
                      <div className="space-y-6">
                        {module.sprints.map((sprint, sprintIdx) => (
                          <div key={sprintIdx} className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
                            <h4 className="text-lg font-bold text-purple-700 mb-4">{sprint.name}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {sprint.lessons.map((lesson, lessonIdx) => (
                                <div 
                                  key={lessonIdx} 
                                  className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02] border border-gray-100"
                                >
                                  <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg flex items-center justify-center font-bold text-xs">
                                    {lessonIdx + 1}
                                  </div>
                                  <span className="text-gray-800 leading-relaxed font-medium text-sm">
                                    {lesson}
                                  </span>
                                </div>
                              ))}
                            </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Tools & Technologies Section */}
      <section className="pt-20 pb-12 px-4" style={{ backgroundColor: '#F1EFED' }}>
        {/* Full Width Separator Line */}
        <div className="w-screen h-px bg-gray-300 mb-16"></div>
        
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Master the <span className="text-purple-600">Creative AI Stack</span><br />
              That Powers <span className="text-green-600">Industry Leaders</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              <span className="text-purple-600 font-semibold">50+ powerful tools</span> for creating stunning content across text, visuals, audio, and video. No coding required - just creative thinking!
            </p>
          </div>

          {/* Scrolling Tools Carousel */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll-left space-x-6" style={{ '--animation-duration': '30s' } as React.CSSProperties}>
              {/* First set of tools */}
              <div className="flex space-x-6 flex-shrink-0">
                {[
                  { name: "Claude", logo: "https://logo.clearbit.com/anthropic.com", category: "Anthropic AI" },
                  { name: "n8n", logo: "https://logo.clearbit.com/n8n.io", category: "Workflow Automation" },
                  { name: "Make", logo: "https://logo.clearbit.com/make.com", category: "Visual Automation" },
                  { name: "Zapier", logo: "https://logo.clearbit.com/zapier.com", category: "App Integration" },
                  { name: "Supabase", logo: "https://logo.clearbit.com/supabase.com", category: "Vector Database" },
                  { name: "Pinecone", logo: "https://logo.clearbit.com/pinecone.io", category: "Vector Search" },
                  { name: "ChatGPT", logo: "https://logo.clearbit.com/openai.com", category: "OpenAI" },
                  { name: "Midjourney", logo: "https://logo.clearbit.com/midjourney.com", category: "AI Art" },
                  { name: "DALL-E", logo: "https://logo.clearbit.com/openai.com", category: "Image Generation" },
                  { name: "RunwayML", logo: "https://logo.clearbit.com/runwayml.com", category: "Video Creation" },
                  { name: "ElevenLabs", logo: "https://logo.clearbit.com/elevenlabs.io", category: "Voice Synthesis" },
                  { name: "Canva", logo: "https://logo.clearbit.com/canva.com", category: "Design Platform" },
                  { name: "Figma", logo: "https://logo.clearbit.com/figma.com", category: "Design Tool" },
                  { name: "Notion", logo: "https://logo.clearbit.com/notion.so", category: "Knowledge Base" },
                  { name: "Stable Diffusion", logo: "https://logo.clearbit.com/stability.ai", category: "Open Source AI" },
                  { name: "Pika Labs", logo: "https://logo.clearbit.com/pika.art", category: "Video Generation" },
                  { name: "Suno AI", logo: "https://logo.clearbit.com/suno.ai", category: "Music Creation" },
                  { name: "Adobe Firefly", logo: "https://logo.clearbit.com/adobe.com", category: "Creative Suite" },
                  { name: "Synthesia", logo: "https://logo.clearbit.com/synthesia.io", category: "AI Video" },
                  { name: "Copy.ai", logo: "https://logo.clearbit.com/copy.ai", category: "Marketing Copy" }
                ].map((tool, idx) => (
                  <div key={idx} className="flex-shrink-0 bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-105 min-w-[200px]">
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 border border-gray-100">
                        <img 
                          src={tool.logo} 
                          alt={tool.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">${tool.name.charAt(0)}</div>`;
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-left text-base truncate">{tool.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{tool.category}</p>
                      </div>
                    </div>
                  </div>
                    ))}
                  </div>
              
              {/* Duplicate set for seamless loop */}
              <div className="flex space-x-6 flex-shrink-0">
                {[
                  { name: "Claude", logo: "https://logo.clearbit.com/anthropic.com", category: "Anthropic AI" },
                  { name: "n8n", logo: "https://logo.clearbit.com/n8n.io", category: "Workflow Automation" },
                  { name: "Make", logo: "https://logo.clearbit.com/make.com", category: "Visual Automation" },
                  { name: "Zapier", logo: "https://logo.clearbit.com/zapier.com", category: "App Integration" },
                  { name: "Supabase", logo: "https://logo.clearbit.com/supabase.com", category: "Vector Database" },
                  { name: "Pinecone", logo: "https://logo.clearbit.com/pinecone.io", category: "Vector Search" },
                  { name: "ChatGPT", logo: "https://logo.clearbit.com/openai.com", category: "OpenAI" },
                  { name: "Midjourney", logo: "https://logo.clearbit.com/midjourney.com", category: "AI Art" },
                  { name: "DALL-E", logo: "https://logo.clearbit.com/openai.com", category: "Image Generation" },
                  { name: "RunwayML", logo: "https://logo.clearbit.com/runwayml.com", category: "Video Creation" },
                  { name: "ElevenLabs", logo: "https://logo.clearbit.com/elevenlabs.io", category: "Voice Synthesis" },
                  { name: "Canva", logo: "https://logo.clearbit.com/canva.com", category: "Design Platform" },
                  { name: "Figma", logo: "https://logo.clearbit.com/figma.com", category: "Design Tool" },
                  { name: "Notion", logo: "https://logo.clearbit.com/notion.so", category: "Knowledge Base" },
                  { name: "Stable Diffusion", logo: "https://logo.clearbit.com/stability.ai", category: "Open Source AI" },
                  { name: "Pika Labs", logo: "https://logo.clearbit.com/pika.art", category: "Video Generation" },
                  { name: "Suno AI", logo: "https://logo.clearbit.com/suno.ai", category: "Music Creation" },
                  { name: "Adobe Firefly", logo: "https://logo.clearbit.com/adobe.com", category: "Creative Suite" },
                  { name: "Synthesia", logo: "https://logo.clearbit.com/synthesia.io", category: "AI Video" },
                  { name: "Copy.ai", logo: "https://logo.clearbit.com/copy.ai", category: "Marketing Copy" }
                ].map((tool, idx) => (
                  <div key={`duplicate-${idx}`} className="flex-shrink-0 bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-105 min-w-[200px]">
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 border border-gray-100">
                        <img 
                          src={tool.logo} 
                          alt={tool.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<div class="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">${tool.name.charAt(0)}</div>`;
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 text-left text-base truncate">{tool.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{tool.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
        {/* Full Width Separator Line */}
        <div className="w-screen h-px bg-gray-300 mb-16"></div>
        
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Frequently Asked <span className="text-purple-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Everything you need to know about becoming a Generative AI Expert
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto">
            <div className="space-y-0">
              {[
                {
                  question: "Do I need any technical background to start this program?",
                  answer: "No technical background required! Our program is designed for creative minds from all backgrounds - artists, writers, marketers, designers, entrepreneurs, and anyone eager to explore AI creativity. We start from the basics and guide you step-by-step to mastery."
                },
                {
                  question: "What makes this different from other AI courses?",
                  answer: "While others focus on technical theory, we emphasize creative application. You'll build real creative projects, launch actual AI-powered services, and develop a portfolio that showcases your creative AI expertise. It's hands-on creativity, not just technical knowledge."
                },
                {
                  question: "What outcomes should I expect from these skills?",
                  answer: "The program is designed to help you build a practical creative AI portfolio, understand modern tools, and apply generative AI to real creative workflows. Individual results depend on your effort, practice, prior experience, and how you apply the skills after the program."
                },
                {
                  question: "How much time do I need to commit each week?",
                  answer: "Plan for 8-12 hours per week for optimal results. This includes live sessions (weekends), hands-on projects, and practice time. The program is designed for working professionals, with flexible scheduling and recorded content for catch-up."
                },
                {
                  question: "What kind of creative projects will I build?",
                  answer: "You'll create diverse projects: AI-generated art portfolios, automated content campaigns, music compositions, video productions, interactive stories, brand identity systems, and commercial applications. Each project builds practical creative production skills."
                },
                {
                  question: "Will I get ongoing support after completing the program?",
                  answer: "Yes. You'll have lifetime access to our Creative AI community, monthly expert sessions, industry updates, and continuous learning resources for continued practice and portfolio development."
                },
                {
                  question: "What if AI tools change or new ones emerge?",
                  answer: "That's the beauty of our approach - we teach you creative thinking and adaptability, not just specific tools. You'll learn the underlying principles that apply to any AI platform, ensuring you stay ahead of the curve as technology evolves."
                }
              ].map((faq, index) => (
                <div key={index} className="border-b border-gray-300 last:border-b-0">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer text-left text-gray-900 hover:text-gray-700 text-lg font-medium py-6 hover:no-underline list-none">
                      <span>{faq.question}</span>
                      <svg 
                        className="w-5 h-5 text-gray-500 transform transition-transform group-open:rotate-180" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </summary>
                    <div className="text-gray-600 pb-6 pt-2 leading-relaxed text-base">
                      {faq.answer}
                    </div>
                  </details>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
    </>
  );
};

export default GenerativeAICourse; 
